import { ApiService } from '../../core/services/api/api.service.js';
import { SessionService } from '../../core/services/session/session.service.js';
import { HostAppEventsService } from './host-app-events.service.js';
import { DebugService } from './debug.service.js';
import { REFI_ESIGN_CONST } from '../../../constants/app.constants.js';

// COAR-53047: toggle that gates the customer-facing automated funding experience.
export const AF_FEATURE_TOGGLE_KEY = 'AUTOMATED_FUNDING_CUSTOMER_FACING';

// Host-registered thinker animations (see refi-digital-host-app-ui getAnimation).
export const AF_ANIMATION = {
  CLIPBOARD: 'clipboard',
  STATEMENT: 'statement',
};

// Entrypoint/application status values returned by the automated-funding-status BFF.
export const AF_ENTRYPOINT_STATUS = {
  COMPLETED: 'COMPLETED',
};
export const AF_APPLICATION_STATUS = {
  FUNDING: 'Funding',
  FUNDED: 'Funded',
};

// Shared footer disclaimer shown on the later thinker stages (per Figma).
export const AF_FOOTER_TEXT = "Hang tight to see your final terms. If you step away, no worries. We'll finish processing it.";

// Ordered thinker stages (COAR-52746). Each stage drives one host animation +
// heading/subtext while we poll the BFF at intervalMs. The stage is always shown
// for at least one polling interval (host honours minimumAnimationLengthMs) and
// gives up after timeoutMs, after which the flow either advances or routes onward.
// Animation names, headings, bullets, and footer copy come from the AF Figma.
export const AF_STAGES = {
  // Stage 1: poll every 3s for up to 20s waiting for the entrypoint to complete.
  WORKING: {
    animationName: AF_ANIMATION.STATEMENT,
    animationText: 'Working on your refinance...',
    intervalMs: 3000,
    timeoutMs: 20000,
  },
  // Stage 2: poll every 5s for up to 30s while the application is still Funding.
  FINALIZING: {
    animationName: AF_ANIMATION.CLIPBOARD,
    animationText: 'Finalizing loan details...',
    animationSubtext: ['Retrieving previous loan balance from your current lender', 'Initiating hard credit check'],
    animationFooterText: AF_FOOTER_TEXT,
    intervalMs: 5000,
    timeoutMs: 30000,
  },
  // Stage 3: poll every 5s for a further 60s while the application is still Funding.
  STILL_WORKING: {
    animationName: AF_ANIMATION.CLIPBOARD,
    animationText: "We're still working on it...",
    animationDescription: 'Just another minute',
    animationFooterText: AF_FOOTER_TEXT,
    intervalMs: 5000,
    timeoutMs: 60000,
  },
};

// Minimum time the "Submitting contract..." submit screen (dispatched by the
// contract component) stays visible before the first thinker stage, so it does not
// flash past on fast responses (per the AF Figma / e2e flow).
export const AF_SUBMITTING_MIN_DISPLAY_MS = 3000;

// R-1.6: per-request timeout for each automated-funding-status poll. The polling
// loop only re-checks its stage timeout / abort flag between requests, so a single
// request must be bounded or a hung BFF call would strand the customer on the
// thinker screen with no way to proceed.
export const AF_REQUEST_TIMEOUT_MS = 10000;

// A setTimeout-based delay that also resolves early if the given signal aborts,
// so a back-navigation can cut short the inter-poll wait instead of stranding
// the customer on the thinker screen for up to a full polling interval.
const delay = (ms, signal) =>
  new Promise(resolve => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    let onAbort;
    const timer = setTimeout(() => {
      if (onAbort) {
        signal?.removeEventListener('abort', onAbort);
      }
      resolve();
    }, ms);
    onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

// AbortSignal.timeout is supported in modern browsers but isn't available in
// every runtime (e.g. the jsdom test environment), so fall back to an
// AbortController-driven timeout when it's missing.
const requestTimeoutSignal = ms => {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
};

export class _AutomatedFundingService {
  constructor() {
    this.debug = new DebugService('AutomatedFundingService');
    this._aborted = false;
    this._failed = false;
    this._abortController = null;
  }

  // Called by the navigation guard (browser back) or the host component's
  // disconnectedCallback to short-circuit the flow. Besides flagging the loop,
  // it aborts the run's AbortController so the pending interval wait and any
  // in-flight BFF request are cancelled immediately instead of racing the
  // host router after the customer has already navigated away.
  abort() {
    this._aborted = true;
    this._abortController?.abort();
  }

  reset() {
    this._aborted = false;
    this._failed = false;
    this._abortController = new AbortController();
  }

  // Combine the per-request timeout with the run's abort signal so either a
  // stalled request (timeout) or a back-navigation (abort) cancels the fetch.
  requestSignal(timeoutMs) {
    const timeout = requestTimeoutSignal(timeoutMs);
    const runSignal = this._abortController?.signal;
    if (!runSignal) {
      return timeout;
    }
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
      return AbortSignal.any([timeout, runSignal]);
    }
    // Runtime without AbortSignal.any: combine manually so a run abort still
    // propagates to the request signal.
    const controller = new AbortController();
    if (timeout.aborted || runSignal.aborted) {
      controller.abort();
    } else {
      const onAbort = () => controller.abort();
      timeout.addEventListener('abort', onAbort, { once: true });
      runSignal.addEventListener('abort', onAbort, { once: true });
    }
    return controller.signal;
  }

  // COAR-52746 / M-2.8: run the thinker flow guarded by a back-navigation
  // listener so a browser back press aborts polling and routes the customer
  // onward. Centralised here so the contract and eTitle call sites share one
  // implementation instead of each managing its own popstate listener.
  async runWithBackGuard(options = {}) {
    const onBack = () => this.abort();
    window.addEventListener('popstate', onBack);
    try {
      await this.runThinkerFlow(options);
    } finally {
      window.removeEventListener('popstate', onBack);
    }
  }

  async getStatus() {
    const encodedEAppId = encodeURIComponent(SessionService.session?.eAppId ?? '');
    return ApiService.get(REFI_ESIGN_CONST.AUTOMATED_FUNDING_STATUS_URL(encodedEAppId), {
      headers: {
        Accept: 'application/json;v=2',
        'Content-Type': 'application/json;v=2',
      },
      // R-1.6: bound each poll so a stalled request can't block the loop/abort,
      // and cancel the in-flight request when the run is aborted (back nav).
      signal: this.requestSignal(AF_REQUEST_TIMEOUT_MS),
    });
  }

  isEntrypointCompleted(status) {
    return String(status?.entrypointStatus ?? '').toUpperCase() === AF_ENTRYPOINT_STATUS.COMPLETED;
  }

  isFunding(status) {
    return status?.applicationStatus === AF_APPLICATION_STATUS.FUNDING;
  }

  isFunded(status) {
    return status?.applicationStatus === AF_APPLICATION_STATUS.FUNDED;
  }

  isFundingOrFunded(status) {
    return this.isFunding(status) || this.isFunded(status);
  }

  isEligible(status) {
    // COAR-52748 contract (AutomatedFundingStatusResponse): eligibility is only
    // reported under eligibilityResult.isAutomatedFundingEligible. entrypointStatus
    // is an uppercase enum ("COMPLETED") and applicationStatus is PascalCase
    // ("Funding"/"Funded"), which is why they are matched differently above.
    return status?.eligibilityResult?.isAutomatedFundingEligible === true;
  }

  sendStageTransition(stage) {
    HostAppEventsService.sendInitiateAnimationTransitionEvent({
      animationName: stage.animationName,
      animationText: stage.animationText,
      animationDescription: stage.animationDescription,
      animationSubtext: stage.animationSubtext,
      animationFooterText: stage.animationFooterText,
      minimumAnimationLengthMs: stage.intervalMs,
      skipCurtainCloseAnimation: true,
    });
  }

  // Polls getStatus() at stage.intervalMs while keepPolling(status) is true,
  // stopping early on abort or when stage.timeoutMs elapses. Returns the last
  // status observed so the caller can decide how to transition. On a BFF error
  // it sets this._failed so runThinkerFlow routes the customer to wrap-up
  // instead of falling through to the next stage with a stale status.
  async pollStage(stage, keepPolling) {
    const started = Date.now();
    let status;
    try {
      status = await this.getStatus();
    } catch (err) {
      console.error('automated funding poll error (initial)', err);
      this._failed = true;
      return status;
    }
    // Check elapsed + one interval against the timeout *before* waiting so the
    // loop stops at the last poll that still fits inside stage.timeoutMs rather
    // than firing an extra poll just past it (ticket cadence: e.g. stage 1 is
    // 7 polls within 20s).
    while (!this._aborted && keepPolling(status) && Date.now() - started + stage.intervalMs <= stage.timeoutMs) {
      await delay(stage.intervalMs, this._abortController?.signal);
      if (this._aborted) {
        break;
      }
      try {
        status = await this.getStatus();
      } catch (err) {
        console.error('automated funding poll error', err);
        this._failed = true;
        break;
      }
    }
    return status;
  }

  // COAR-52746: run the staged thinker sequence after contract submission.
  // Resolves once the customer should be routed onward (via fetch-mfe-order):
  // on terminal status, poll timeout, back-navigation, or any BFF error. The
  // caller performs the actual routing + success animation; this method never
  // throws so a polling failure cannot undo the already-successful signing.
  async runThinkerFlow({ holdSubmittingScreenMs = AF_SUBMITTING_MIN_DISPLAY_MS } = {}) {
    this.reset();

    try {
      // Keep the "Submitting contract..." submit screen visible briefly before the
      // first thinker stage so it does not flash past on fast responses. The eTitle
      // (POATA) path passes 0 to go straight to the "Working on your refinance..."
      // stage, since it has no preceding submit screen.
      if (holdSubmittingScreenMs > 0) {
        await delay(holdSubmittingScreenMs, this._abortController?.signal);
      }
      if (this._aborted) {
        return;
      }

      // Stage 1 — wait for the automated-funding entrypoint to complete.
      this.sendStageTransition(AF_STAGES.WORKING);
      const stageOne = await this.pollStage(AF_STAGES.WORKING, status => !this.isEntrypointCompleted(status));
      if (this._aborted || this._failed) {
        return;
      }
      // Only eligible applications that are still Funding continue the thinker;
      // every other outcome (ineligible, already Funded, other status, timeout)
      // routes onward immediately.
      if (!(this.isEntrypointCompleted(stageOne) && this.isEligible(stageOne) && this.isFunding(stageOne))) {
        return;
      }

      // Stage 2 — keep the customer informed while the loan is still Funding.
      this.sendStageTransition(AF_STAGES.FINALIZING);
      const stageTwo = await this.pollStage(AF_STAGES.FINALIZING, status => this.isFunding(status));
      if (this._aborted || this._failed || !this.isFunding(stageTwo)) {
        return;
      }

      // Stage 3 — still Funding after stage 2, extend the thinker once more.
      this.sendStageTransition(AF_STAGES.STILL_WORKING);
      await this.pollStage(AF_STAGES.STILL_WORKING, status => this.isFunding(status));
    } catch (error) {
      // R-1.2: fail open (route onward) but surface the error in production.
      // debug.log is suppressed when isProdEnv, which would hide BFF/timeout
      // failures entirely; console.error keeps them observable.
      console.error('automated funding flow error, routing onward', error);
    }
  }
}

export const AutomatedFundingService = new _AutomatedFundingService();
